import type { MaybeRefOrGetter } from 'vue'
import EventBus from '@/components/app/AppEventBus'

const ACTIVE_SECTION_OFFSET = 100
const BOTTOM_TOLERANCE = 4

type AccountSection = { id: string; label: string; detailRoute?: string }

export const ACCOUNT_SECTIONS: AccountSection[] = [
	{ id: 'profileSection', label: 'auth.profile' },
	{ id: 'activitySkySection', label: 'auth.activity_sky', detailRoute: '/account/activity-sky' },
	{ id: 'interestsSection', label: 'auth.interests' },
	{ id: 'personalitySection', label: 'auth.personality' },
	{ id: 'transparencySection', label: 'auth.transparency' },
	{ id: 'signInSettingsSection', label: 'auth.sign_in_settings' },
]

const LAST_SECTION = ACCOUNT_SECTIONS[ACCOUNT_SECTIONS.length - 1]
const FIRST_SECTION = ACCOUNT_SECTIONS[0]

export const useAccountSectionNav = (menuOpen: MaybeRefOrGetter<boolean>) => {
	const route = useRoute()
	const localePath = useLocalePath()
	const mounted = useMounted()
	const { y: scrollY } = useWindowScroll()

	const profilePath = computed(() => localePath('/account/profile'))

	const scrolledSection = computed(() => {
		if (!mounted.value || route.path !== profilePath.value) return ''
		if (scrollY.value + window.innerHeight >= document.documentElement.scrollHeight - BOTTOM_TOLERANCE) {
			return LAST_SECTION.id
		}
		return ACCOUNT_SECTIONS.reduce((current, { id }) => {
			const top = document.getElementById(id)?.getBoundingClientRect().top
			return top !== undefined && top <= ACTIVE_SECTION_OFFSET ? id : current
		}, FIRST_SECTION.id)
	})

	const pinnedSection = ref('')
	useEventListener(['wheel', 'touchmove', 'keydown'], () => { pinnedSection.value = '' }, { passive: true })
	watch(() => route.fullPath, () => {
		pinnedSection.value = ACCOUNT_SECTIONS.find(({ id }) => route.hash === `#${id}`)?.id ?? ''
	})

	const sectionLink = (sectionId: string) => `${profilePath.value}#${sectionId}`

	const isSectionActive = (sectionId: string) => {
		const { detailRoute } = ACCOUNT_SECTIONS.find(({ id }) => id === sectionId) ?? {}
		if (detailRoute && route.path.startsWith(localePath(detailRoute) + '/')) return true
		return (pinnedSection.value || scrolledSection.value) === sectionId
	}

	const openSection = (sectionId: string) => {
		pinnedSection.value = sectionId
		if (toValue(menuOpen)) EventBus.emit('main-menu-clicked')
		if (route.hash === `#${sectionId}`) {
			document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' })
		}
	}

	return { sections: ACCOUNT_SECTIONS, sectionLink, isSectionActive, openSection }
}
